import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import {
  Typography,
  TextField,
  Paper,
  IconButton,
} from '@material-ui/core';
import { ReplyOutlined } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { getProductGroup, putEditProductGroup, postNewProductGroup } from '../../../requests/api/product-groups';
import useStyles from './styles';
import Button from '../../../components/Button';
import Snackbar from '../../../components/Snackbar';
import axios from '../../../utils/axios';
import AssociatedProductsTables from '../Components/AssociatedProductsTables';

const ProductGroupsDetailView = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { mode = 'create', productGroupId } = useParams();

  const [companyId, setCompanyId] = useState(axios?.defaults?.params?.companyId);
  const [formData, setFormData] = useState({ name: '', description: '', companyId });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isEditing, setIsEditing] = useState(mode !== 'detail');
  const [associatedProducts, setAssociatedProducts] = useState([]);

  const isDetail = mode === 'detail';
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';

  // Company ID setup
  useEffect(() => {
    if (companyId) return;
    const interval = setInterval(() => {
      const currentId = axios?.defaults?.params?.companyId;
      if (currentId !== undefined) {
        setFormData({ ...formData, companyId: currentId });
        setCompanyId(currentId);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [companyId, formData]);

  const fetchProductGroup = useCallback(async () => {
    if (!productGroupId || (!isDetail && !isEdit)) return;

    setLoading(true);
    try {
      const response = await getProductGroup(productGroupId);
      if (response.success) {
        setFormData({
          name: response.data.data.productGroup.name || '',
          description: response.data.data.productGroup.description || '',
          companyId,
        });
        setAssociatedProducts(
          response.data.data.productGroup.Products || [],
        );
      } else {
        setMessage(t('common.failedLoad'));
      }
    } catch (error) {
      setMessage(t('common.failedLoad'));
    } finally {
      setLoading(false);
    }
  }, [productGroupId, isDetail, isEdit, companyId, t]);

  useEffect(() => {
    fetchProductGroup();
  }, [fetchProductGroup]);

  const handleInputChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      let response;
      const body = { ...formData, productIds: associatedProducts.map((p) => p.id) };
      if (isCreate) {
        response = await postNewProductGroup(body);
      } else {
        response = await putEditProductGroup(productGroupId, body);
      }
      if (response.success) {
        setMessage(t('common.savedSuccessfully'));
        if (isCreate) {
          history.push(`/product-groups/${response.data.data.productGroup.id}/detail`);
        } else {
          setIsEditing(false);
        }
      } else {
        setMessage(t('common.savedUnsuccessfully'));
      }
    } catch (error) {
      setMessage(t('common.savedUnsuccessfully'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const getTitle = () => {
    if (isCreate) return t('productGroup.createTitle');
    if (isEditing) return t('productGroup.editTitle');
    return t('productGroup.detailTitle');
  };

  const getButtonText = () => {
    if (isEditing) return t('common.save');
    return t('common.edit');
  };

  const handleButtonClick = () => {
    if (isEditing) {
      handleSave();
    } else {
      handleEdit();
    }
  };

  return (
    <div className={styles.content}>
      <div className={styles.header}>
        <Typography variant='h4' className={styles.title}>
          {getTitle()}
        </Typography>
        <div className={styles.buttonRow}>
          <IconButton
            id="backButton"
            aria-label="back"
            className={styles.backButton}
            component={Link}
            to={'/product-groups'}
          >
            <ReplyOutlined fontSize="small" />
            <span>{t('productGroup.comeBackTitle')}</span>
          </IconButton>
          <Button
            id="action-btn"
            variant="outlined"
            className={styles.createButton}
            text={getButtonText()}
            height="40px"
            width="220px"
            backgroundColor='theme-custom-solidButtonBackground'
            color='theme-custom-solidButtonText'
            borderRadius='10px'
            onClick={handleButtonClick}
            disabled={loading}
          />
        </div>
      </div>
      <Paper className={styles.paper}>
        <TextField
          label="Nombre"
          value={formData.name}
          onChange={handleInputChange('name')}
          fullWidth
          margin="normal"
          variant="outlined"
          disabled={!isEditing}
          required
          className={styles.textField}
        />
        <TextField
          label="Descripción"
          value={formData.description}
          onChange={handleInputChange('description')}
          fullWidth
          margin="normal"
          variant="outlined"
          multiline
          minRows={4}
          disabled={!isEditing}
          className={styles.textField}
        />
        <AssociatedProductsTables
          associatedProducts={associatedProducts}
          setAssociatedProducts={setAssociatedProducts}
          isEditing={isEditing}
        />
      </Paper>
      <Snackbar
        open={message !== ''}
        message={message}
        severity={message.includes('Error') ? 'error' : 'success'}
        onClose={() => setMessage('')}
      />
    </div>
  );
};

export default ProductGroupsDetailView;
